import React, { useRef, useEffect, useState } from 'react'
import * as THREE from 'three'
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js'
import { TransformControls } from 'three/examples/jsm/controls/TransformControls.js'
import { Line2 } from 'three/examples/jsm/lines/Line2.js'
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js'
import { LineGeometry } from 'three/examples/jsm/lines/LineGeometry.js'
import { STLLoadResult } from '../../utils/stlLoader'
import { loadViewerState, autoSaveViewerState, applyCameraState } from '../../utils/statePersistence'
import { Landmark } from '../../services/backendService'
import './Canvas3D.css'

// Ensure Three.js is available globally for debugging
if (typeof window !== 'undefined') {
  (window as any).THREE = THREE
  ;(window as any).OrbitControls = OrbitControls
}

interface SceneObject {
  id: string
  name: string
  type: 'mesh' | 'points' | 'template' | 'grid' | 'annotation'
  visible: boolean
  selectable: boolean
}

interface SelectedPoint {
  id: string
  position: { x: number; y: number; z: number }
  timestamp: number
  type?: 'point' | 'measure'
}

interface TemplateOverlay {
  id: string
  name: string
  points: number[][]
  landmarks?: Record<string, number[]>
  visible: boolean
  color?: string
  /** Centroid of the un-adjusted aligned points; preserves user translation. */
  originCentroid?: [number, number]
  /** Optional Z offset (mm) applied to the rendered overlay group in 3D. */
  zOffset?: number
}

interface Canvas3DProps {
  objects: SceneObject[]
  selectedTool: string | null
  stlData?: STLLoadResult
  selectedPoints: SelectedPoint[]
  measurePoints?: SelectedPoint[]
  measureDistance?: number | null
  onPointSelect: (point: { x: number; y: number; z: number }) => void
  onSceneObjectsChange?: (objects: SceneObject[]) => void
  selectedObjectId?: string | null
  calibrationLandmarks?: Landmark[]
  showCalibrationLandmarks?: boolean
  isEditingCalibrationLandmarks?: boolean
  onCalibrationLandmarkUpdate?: (landmarkId: string, newPosition: { x: number; y: number; z: number }) => void
  /** Which calibration point is being re-picked (null = none, 'heel'|'mt1'|'mt5') */
  pickingCalibrationPoint?: string | null
  /** Called when user clicks the surface while picking mode is active */
  onCalibrationPointPicked?: (landmarkId: string, position: { x: number; y: number; z: number }) => void
  templateOverlay?: TemplateOverlay | null
  /** Transform mode for the gizmo when an object is selected. */
  transformMode?: 'translate' | 'rotate' | 'scale'
  // V2 template transform delta — dx/dy/dz in scene units, drot in radians,
  // dscale as (newScale/oldScale - 1).
  /** Fired while the user drags the gizmo. dx/dy in world mm; drot in radians. */
  onTemplateTransform?: (delta: { dx: number; dy: number; dz: number; drot: number; dscale: number }) => void
  /** Fired when a gizmo drag ends (so callers can commit/finalize state). */
  onTemplateTransformEnd?: () => void
}

// Sample objects removed - only STL files should be displayed in the viewer

export const Canvas3D: React.FC<Canvas3DProps> = ({
  objects,
  selectedTool,
  stlData,
  selectedPoints,
  measurePoints = [],
  measureDistance = null,
  onPointSelect,
  onSceneObjectsChange,
  selectedObjectId,
  calibrationLandmarks = [],
  showCalibrationLandmarks = false,
  isEditingCalibrationLandmarks = false,
  onCalibrationLandmarkUpdate,
  pickingCalibrationPoint = null,
  onCalibrationPointPicked,
  templateOverlay = null,
  transformMode = 'translate',
  onTemplateTransform,
  onTemplateTransformEnd
}) => {
  console.log('Canvas3D: Component rendering...')

  // Track the last loaded STL data to detect changes
  const lastStlDataRef = useRef<STLLoadResult | undefined>(undefined)

  const mountRef = useRef<HTMLDivElement>(null)
  const sceneRef = useRef<THREE.Scene>()
  const rendererRef = useRef<THREE.WebGLRenderer>()
  const cameraRef = useRef<THREE.PerspectiveCamera>()
  const controlsRef = useRef<OrbitControls>()
  const transformControlsRef = useRef<TransformControls | null>(null)
  const transformControlsHelperRef = useRef<THREE.Object3D | null>(null)
  const transformProxyRef = useRef<THREE.Object3D | null>(null)

  // Track which calibration landmark is currently being dragged
  const selectedCalibrationLandmarkIdRef = useRef<string | null>(null)
  const setSelectedCalibrationLandmarkId = (id: string | null) => { selectedCalibrationLandmarkIdRef.current = id }
  // Scene-space centroid + plantar Z of the rendered template overlay,
  // captured by the overlay-render effect so the gizmo proxy can be placed
  // exactly on top of the visible cyan loop instead of at raw mm coords.
  const templateAnchorRef = useRef<{ x: number; y: number; z: number } | null>(null)
  const transformDragStartRef = useRef<{ x: number; y: number; z: number; rot: number; scale: number } | null>(null)
  const frameRef = useRef<number>()
  const raycasterRef = useRef<THREE.Raycaster>(new THREE.Raycaster())
  const mouseRef = useRef<THREE.Vector2>(new THREE.Vector2())
  const clickHandlerRef = useRef<((event: MouseEvent) => void) | null>(null)
  const contextLostHandlerRef = useRef<((event: Event) => void) | null>(null)
  const contextRestoredHandlerRef = useRef<(() => void) | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isInitialized, setIsInitialized] = useState(false)
  const stlMeshRef = useRef<THREE.Mesh | null>(null)

  // Helper function to extract scene objects from Three.js scene
  const extractSceneObjects = (scene: THREE.Scene): SceneObject[] => {
    const sceneObjects: SceneObject[] = []

    scene.children.forEach(child => {
      // Skip lights, cameras, and point markers
      if (child.userData.type === 'point-marker') return
      if (child instanceof THREE.Light) return
      if (child instanceof THREE.Camera) return

      // Add actual scene objects
      if (child.userData.id) {
        sceneObjects.push({
          id: child.userData.id,
          name: child.userData.name || child.userData.id,
          type: child.userData.objectType || 'mesh',
          visible: child.visible,
          selectable: child.userData.selectable !== false
        })
      }
    })

    return sceneObjects
  }

  // Helper function to load STL mesh into scene
  const loadSTLMesh = (stlLoadResult: STLLoadResult) => {
    if (!sceneRef.current) {
      console.error('Canvas3D: Cannot load STL - scene not initialized')
      return
    }

    console.log('Canvas3D: Loading STL mesh into scene...')

    // Remove existing STL mesh if any
    if (stlMeshRef.current) {
      console.log('Canvas3D: Removing existing STL mesh')
      sceneRef.current.remove(stlMeshRef.current)
      stlMeshRef.current.geometry.dispose()
      if (Array.isArray(stlMeshRef.current.material)) {
        stlMeshRef.current.material.forEach(mat => mat.dispose())
      } else {
        stlMeshRef.current.material.dispose()
      }
      stlMeshRef.current = null
    }

    // Add new STL mesh (deep-clone geometry to avoid disposing the shared original)
    const stlMesh = stlLoadResult.mesh.clone()
    stlMesh.geometry = stlLoadResult.mesh.geometry.clone()
    stlMesh.name = 'stl-object'
    stlMesh.userData = {
      id: 'stl-mesh',
      name: 'STL Mesh',
      objectType: 'mesh',
      selectable: true
    }
    sceneRef.current.add(stlMesh)
    stlMeshRef.current = stlMesh

    // Report updated scene objects to parent
    if (onSceneObjectsChange) {
      const updatedObjects = extractSceneObjects(sceneRef.current)
      console.log('Canvas3D: Reporting scene objects after STL load:', updatedObjects)
      onSceneObjectsChange(updatedObjects)
    }

    console.log('Canvas3D: STL mesh loaded successfully')
  }

  // Initialize Three.js scene function
  const initializeThreeJS = (mountElement: HTMLDivElement) => {
    if (isInitialized) {
      console.log('Canvas3D: Already initialized, skipping...')
      return
    }

    console.log('Canvas3D: Starting initialization...')
    console.log('Canvas3D: THREE object available:', typeof THREE !== 'undefined')
    console.log('Canvas3D: Mount element available:', !!mountElement)

    // Check if Three.js is properly loaded
    if (typeof THREE === 'undefined') {
      console.error('Canvas3D: THREE is not defined')
      setError('Three.js library not loaded properly')
      setIsLoading(false)
      return
    }

    try {
      console.log('Canvas3D: Creating Three.js scene...')
      // Scene setup
      const scene = new THREE.Scene()
      scene.background = new THREE.Color(0x2a2a2a)
      sceneRef.current = scene
      console.log('Canvas3D: Scene created successfully')

      // Camera setup
      console.log('Canvas3D: Creating camera...')
      const camera = new THREE.PerspectiveCamera(
        75,
        mountElement.clientWidth / mountElement.clientHeight,
        0.1,
        1000
      )
      camera.position.set(5, 5, 5)
      camera.lookAt(0, 0, 0)
      cameraRef.current = camera
      console.log('Canvas3D: Camera created successfully')

      // Renderer setup with WebGL context loss handling
      console.log('Canvas3D: Creating WebGL renderer...')
      const renderer = new THREE.WebGLRenderer({
        antialias: true,
        alpha: true,
        preserveDrawingBuffer: false,
        powerPreference: "high-performance",
        failIfMajorPerformanceCaveat: false
      })
      console.log('Canvas3D: WebGL renderer created successfully')
      renderer.setSize(mountElement.clientWidth, mountElement.clientHeight)
      renderer.setPixelRatio(Math.min(window.devicePixelRatio, 3))
      renderer.shadowMap.enabled = true
      renderer.shadowMap.type = THREE.PCFSoftShadowMap
      renderer.outputColorSpace = THREE.SRGBColorSpace
      rendererRef.current = renderer

      // Handle WebGL context loss
      const handleContextLost = (event: Event) => {
        event.preventDefault()
        console.warn('WebGL context lost in Girasoles viewer')
        setError('WebGL context lost. Please refresh the page.')
      }

      const handleContextRestored = () => {
        console.log('WebGL context restored in Girasoles viewer')
        setError(null)
      }

      contextLostHandlerRef.current = handleContextLost
      contextRestoredHandlerRef.current = handleContextRestored
      renderer.domElement.addEventListener('webglcontextlost', handleContextLost)
      renderer.domElement.addEventListener('webglcontextrestored', handleContextRestored)

      // Add renderer to DOM
      console.log('Canvas3D: Adding renderer to DOM...')
      mountElement.appendChild(renderer.domElement)
      console.log('Canvas3D: Renderer added to DOM successfully')

      // Camera controls
      console.log('Canvas3D: Creating OrbitControls...')
      console.log('Canvas3D: OrbitControls available:', typeof OrbitControls !== 'undefined')

      if (typeof OrbitControls === 'undefined') {
        console.error('Canvas3D: OrbitControls is not defined')
        setError('OrbitControls not loaded properly')
        setIsLoading(false)
        return
      }

      const controls = new OrbitControls(camera, renderer.domElement)
      controls.enableDamping = true
      controls.dampingFactor = 0.05
      controls.screenSpacePanning = false
      controls.minDistance = 2
      controls.maxDistance = 50
      controls.maxPolarAngle = Math.PI / 2
      controlsRef.current = controls
      console.log('Canvas3D: OrbitControls created successfully')

      // TransformControls (gizmo) — attached on demand
      try {
        const tcAny = new (TransformControls as any)(camera, renderer.domElement) as THREE.Object3D & {
          addEventListener: (type: string, listener: (event: any) => void) => void
          attach: (object: THREE.Object3D) => void
          detach: () => void
          setMode: (mode: 'translate' | 'rotate' | 'scale') => void
          showX: boolean
          showY: boolean
          showZ: boolean
          enabled: boolean
          getHelper?: () => THREE.Object3D
        }
        // r170+ requires the helper to be added separately. The helper
        // is the actual scene-graph object that renders the gizmo, so its
        // .visible flag (NOT tc.visible) is what controls display.
        const helper = (tcAny as any).getHelper ? (tcAny as any).getHelper() : tcAny
        scene.add(helper as THREE.Object3D)
        ;(helper as THREE.Object3D).visible = false
        transformControlsHelperRef.current = helper as THREE.Object3D
        tcAny.setMode('translate')
        // Translate gizmo defaults to all 3 axes (X/Y/Z). Mode-specific axis
        // visibility is reapplied by the transformMode effect below.
        tcAny.showX = true
        tcAny.showY = true
        tcAny.showZ = true
        tcAny.enabled = false
        tcAny.addEventListener('dragging-changed', (event: any) => {
          if (controlsRef.current) controlsRef.current.enabled = !event.value
          if (!event.value) {
            // drag ended — reset proxy to origin and notify caller
            const proxy = transformProxyRef.current
            if (proxy) { proxy.position.set(0, 0, 0); proxy.rotation.set(0, 0, 0); proxy.scale.set(1, 1, 1) }
            transformDragStartRef.current = null
            if (onTemplateTransformEnd) onTemplateTransformEnd()
          } else {
            const proxy = transformProxyRef.current
            transformDragStartRef.current = proxy
              ? { x: proxy.position.x, y: proxy.position.y, z: proxy.position.z, rot: proxy.rotation.z, scale: proxy.scale.x }
              : { x: 0, y: 0, z: 0, rot: 0, scale: 1 }
          }
        })
        tcAny.addEventListener('objectChange', () => {
          const proxy = transformProxyRef.current
          const start = transformDragStartRef.current
          if (!proxy || !start) return
          const delta = {
            dx: proxy.position.x - start.x,
            dy: proxy.position.y - start.y,
            dz: proxy.position.z - start.z,
            drot: proxy.rotation.z - start.rot,
            dscale: proxy.scale.x / Math.max(start.scale, 1e-6) - 1,
          }
          if (onTemplateTransform) onTemplateTransform(delta)
        })
        transformControlsRef.current = tcAny as unknown as TransformControls
      } catch (err) {
        console.warn('Canvas3D: Failed to initialize TransformControls', err)
      }

      // ── Dev-only debug hooks ──────────────────────────────────────────
      // Expose live Three.js objects on window for Chrome DevTools MCP
      // inspection. Guarded by Vite's import.meta.env.DEV flag — never
      // active in production builds. Use window.__scene, __renderer,
      // __camera, __controls to query live state via evaluate_script.
      if (import.meta.env.DEV) {
        const win = window as any
        win.__scene = scene
        win.__renderer = renderer
        win.__camera = camera
        win.__controls = controls
        console.log('[DEV] Three.js debug hooks exposed on window.__scene, __renderer, __camera, __controls')
      }

      // Auto-save state when camera changes (with debouncing)
      controls.addEventListener('change', () => {
        autoSaveViewerState(camera, selectedPoints, objects, selectedTool, 2000)
      })

      // Publish current view (azim/elev/dist + nearest canonical-axis label)
      // for the bottom-right Navigator panel. Keep math in scene-Y-up
      // convention so labels are stable regardless of which axis the foot
      // STL was authored along.
      const dispatchViewChanged = () => {
        const tgt = controls.target
        const dx = camera.position.x - tgt.x
        const dy = camera.position.y - tgt.y
        const dz = camera.position.z - tgt.z
        const dist = Math.sqrt(dx * dx + dy * dy + dz * dz)
        const horiz = Math.sqrt(dx * dx + dz * dz)
        // Azimuth: 0° looking toward -Z (i.e. camera at +Z), 90° at +X.
        const azim = (Math.atan2(dx, dz) * 180) / Math.PI
        const elev = (Math.atan2(dy, horiz) * 180) / Math.PI
        // Nearest canonical view label (within 15° tolerance on both axes).
        let label = 'ISO'
        const tol = 15
        if (Math.abs(elev - 90) < tol) label = '+Y (Top)'
        else if (Math.abs(elev + 90) < tol) label = '-Y (Bottom)'
        else if (Math.abs(elev) < tol) {
          const a = ((azim % 360) + 360) % 360
          if (a < tol || a > 360 - tol) label = '+Z (Front)'
          else if (Math.abs(a - 180) < tol) label = '-Z (Back)'
          else if (Math.abs(a - 90) < tol) label = '+X (Right)'
          else if (Math.abs(a - 270) < tol) label = '-X (Left)'
        }
        window.dispatchEvent(new CustomEvent('gv:view-changed', {
          detail: { azim, elev, dist, label }
        }))
      }
      controls.addEventListener('change', dispatchViewChanged)
      // Emit one initial event so the panel has data on mount.
      setTimeout(dispatchViewChanged, 0)

      // Snap to canonical views from the Navigator panel.
      const onSnapView = (e: Event) => {
        const view = (e as CustomEvent).detail?.view as string | undefined
        if (!view || !cameraRef.current || !controlsRef.current) return
        const cam = cameraRef.current
        const ctl = controlsRef.current
        // Preserve the current radius from target so zoom stays the same.
        const r = Math.max(2, cam.position.distanceTo(ctl.target))
        const t = ctl.target
        // Allow looking from below by widening polar range while we snap.
        const prevMaxPolar = ctl.maxPolarAngle
        const prevMinPolar = ctl.minPolarAngle
        ctl.maxPolarAngle = Math.PI
        ctl.minPolarAngle = 0
        switch (view) {
          case '+Y': cam.position.set(t.x, t.y + r, t.z + 0.001); break
          case '-Y': cam.position.set(t.x, t.y - r, t.z + 0.001); break
          case '+Z': cam.position.set(t.x, t.y, t.z + r); break
          case '-Z': cam.position.set(t.x, t.y, t.z - r); break
          case '+X': cam.position.set(t.x + r, t.y, t.z); break
          case '-X': cam.position.set(t.x - r, t.y, t.z); break
          case 'ISO': {
            const k = r / Math.sqrt(3)
            cam.position.set(t.x + k, t.y + k, t.z + k)
            break
          }
        }
        cam.up.set(0, 1, 0)
        cam.lookAt(t)
        ctl.update()
        // Restore previous polar limits AFTER update so the next user
        // interaction still respects the original limits if they care.
        ctl.maxPolarAngle = prevMaxPolar
        ctl.minPolarAngle = prevMinPolar
        dispatchViewChanged()
      }
      window.addEventListener('gv:snap-view', onSnapView as EventListener)
      // Cleanup is registered later in the main effect's return; keep a
      // reference so we can remove it.
      ;(controls as any).__onSnapView = onSnapView

      // Lighting
      const ambientLight = new THREE.AmbientLight(0x404040, 0.6)
      scene.add(ambientLight)

      const directionalLight = new THREE.DirectionalLight(0xffffff, 0.8)
      directionalLight.position.set(10, 10, 5)
      directionalLight.castShadow = true
      directionalLight.shadow.mapSize.width = 2048
      directionalLight.shadow.mapSize.height = 2048
      scene.add(directionalLight)

      // Add STL mesh if provided during initialization
      if (stlData) {
        loadSTLMesh(stlData)
        lastStlDataRef.current = stlData
      }

      // Click event listener is now handled in a separate useEffect
      // that updates when selectedTool changes

      // Load saved state if available
      console.log('Canvas3D: Loading saved state...')
      const savedState = loadViewerState()
      if (savedState) {
        applyCameraState(camera, savedState.camera)
        controls.update()
        console.log('Canvas3D: Saved state applied')
      } else {
        console.log('Canvas3D: No saved state found')
      }

      console.log('Canvas3D: Initialization completed successfully!')
      setIsInitialized(true)
      setIsLoading(false)
    } catch (err) {
      console.error('Canvas3D: Failed to initialize Three.js:', err)
      console.error('Canvas3D: Error details:', err)
      setError(`Failed to initialize 3D scene: ${err instanceof Error ? err.message : 'Unknown error'}`)
      setIsLoading(false)
    }
  }

  // Initialize on mount
  useEffect(() => {
    console.log('Canvas3D: useEffect triggered, isInitialized:', isInitialized)
    if (!isInitialized && mountRef.current) {
      console.log('Canvas3D: Mount element available, initializing...')
      initializeThreeJS(mountRef.current)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInitialized])

  // Cleanup effect
  useEffect(() => {
    const mountNode = mountRef.current
    return () => {
      // Cleanup
      if (frameRef.current) {
        cancelAnimationFrame(frameRef.current)
      }
      if (controlsRef.current) {
        const onSnapView = (controlsRef.current as any).__onSnapView as
          | EventListener
          | undefined
        if (onSnapView) window.removeEventListener('gv:snap-view', onSnapView)
        controlsRef.current.dispose()
      }
      if (rendererRef.current && mountNode) {
        if (clickHandlerRef.current) {
          rendererRef.current.domElement.removeEventListener('click', clickHandlerRef.current)
        }
        // Remove WebGL context event listeners
        if (contextLostHandlerRef.current) {
          rendererRef.current.domElement.removeEventListener('webglcontextlost', contextLostHandlerRef.current)
        }
        if (contextRestoredHandlerRef.current) {
          rendererRef.current.domElement.removeEventListener('webglcontextrestored', contextRestoredHandlerRef.current)
        }
        if (mountNode.contains(rendererRef.current.domElement)) {
          mountNode.removeChild(rendererRef.current.domElement)
        }
        rendererRef.current.dispose()
      }
    }
  }, [])

  // Update OrbitControls based on selected tool
  useEffect(() => {
    if (!controlsRef.current) return

    const controls = controlsRef.current

    // Configure controls based on selected tool
    if (selectedTool === 'move') {
      // Move tool: left-click to pan, disable rotation
      controls.mouseButtons = {
        LEFT: THREE.MOUSE.PAN,
        MIDDLE: THREE.MOUSE.DOLLY,
        RIGHT: THREE.MOUSE.ROTATE
      }
      console.log('Canvas3D: Move tool active - left-click to pan')
    } else if (selectedTool === 'rotate') {
      // Rotate tool: left-click to rotate (default behavior)
      controls.mouseButtons = {
        LEFT: THREE.MOUSE.ROTATE,
        MIDDLE: THREE.MOUSE.DOLLY,
        RIGHT: THREE.MOUSE.PAN
      }
      console.log('Canvas3D: Rotate tool active - left-click to rotate')
    } else {
      // Default: left-click to rotate, right-click to pan
      controls.mouseButtons = {
        LEFT: THREE.MOUSE.ROTATE,
        MIDDLE: THREE.MOUSE.DOLLY,
        RIGHT: THREE.MOUSE.PAN
      }
    }
  }, [selectedTool])

  // Update click handler when selectedTool changes
  useEffect(() => {
    if (!rendererRef.current || !cameraRef.current || !sceneRef.current) return

    const handleCanvasClick = (event: MouseEvent) => {
      // Priority 1: Calibration point picking mode
      if (pickingCalibrationPoint && onCalibrationPointPicked) {
        const rect = rendererRef.current!.domElement.getBoundingClientRect()
        mouseRef.current.x = ((event.clientX - rect.left) / rect.width) * 2 - 1
        mouseRef.current.y = -((event.clientY - rect.top) / rect.height) * 2 + 1

        raycasterRef.current.setFromCamera(mouseRef.current, cameraRef.current!)

        // Raycast only against the STL foot mesh for surface placement
        const footMesh = sceneRef.current?.getObjectByName('stl-object') as THREE.Mesh | undefined
        if (footMesh) {
          const hits = raycasterRef.current.intersectObject(footMesh, true)
          if (hits.length > 0) {
            const pt = hits[0].point
            onCalibrationPointPicked(pickingCalibrationPoint, { x: pt.x, y: pt.y, z: pt.z })
          }
        }
        return
      }

      // Priority 2: Calibration edit click (select sphere → TransformControls)
      if (isEditingCalibrationLandmarks) {
        const rect = rendererRef.current!.domElement.getBoundingClientRect()
        mouseRef.current.x = ((event.clientX - rect.left) / rect.width) * 2 - 1
        mouseRef.current.y = -((event.clientY - rect.top) / rect.height) * 2 + 1

        raycasterRef.current.setFromCamera(mouseRef.current, cameraRef.current!)

        const calibrationGroup = sceneRef.current?.getObjectByName('calibration-landmarks-group') as THREE.Group
        if (!calibrationGroup || !calibrationGroup.visible) return

        const meshes: THREE.Mesh[] = []
        calibrationGroup.traverse((child) => {
          if (child instanceof THREE.Mesh) meshes.push(child)
        })

        const intersects = raycasterRef.current.intersectObjects(meshes)
        if (intersects.length > 0) {
          const hit = intersects[0].object as THREE.Mesh
          const landmarkId = hit.userData.landmarkId as string
          if (landmarkId) {
            setSelectedCalibrationLandmarkId(landmarkId)
            const tc = transformControlsRef.current as any
            if (tc) {
              tc.attach(hit)
              tc.enabled = true
              tc.visible = true
              const onDragEnd = () => {
                const pos = hit.position.clone()
                const footMesh = sceneRef.current?.getObjectByName('stl-object') as THREE.Mesh | undefined
                if (footMesh) {
                  let bestDist = Infinity
                  let bestPoint: THREE.Vector3 | null = null
                  const center = new THREE.Vector3()
                  footMesh.geometry.computeBoundingBox()
                  footMesh.geometry.boundingBox!.getCenter(center)
                  const directions = [
                    new THREE.Vector3(0, -1, 0), new THREE.Vector3(0, 1, 0),
                    new THREE.Vector3(1, 0, 0), new THREE.Vector3(-1, 0, 0),
                    new THREE.Vector3(0, 0, 1), new THREE.Vector3(0, 0, -1),
                  ]
                  for (const dir of directions) {
                    const origin = pos.clone().addScaledVector(dir, -10)
                    const ray = new THREE.Raycaster(origin, dir)
                    const hits = ray.intersectObject(footMesh, true)
                    if (hits.length > 0) {
                      const d = pos.distanceTo(hits[0].point)
                      if (d < bestDist) { bestDist = d; bestPoint = hits[0].point.clone() }
                    }
                  }
                  if (bestPoint) hit.position.copy(bestPoint)
                }
                const snapped = hit.position
                onCalibrationLandmarkUpdate?.(landmarkId, { x: snapped.x, y: snapped.y, z: snapped.z })
                tc.removeEventListener('mouseUp', onDragEnd)
              }
              tc.addEventListener('mouseUp', onDragEnd)
            }
          }
        } else {
          setSelectedCalibrationLandmarkId(null)
          const tc = transformControlsRef.current as any
          if (tc) { tc.detach(); tc.enabled = false; tc.visible = false }
        }
        return
      }

      // Priority 3: Point/measure tool click
      if (selectedTool !== 'point' && selectedTool !== 'measure') return

      const rect = rendererRef.current!.domElement.getBoundingClientRect()
      mouseRef.current.x = ((event.clientX - rect.left) / rect.width) * 2 - 1
      mouseRef.current.y = -((event.clientY - rect.top) / rect.height) * 2 + 1

      raycasterRef.current.setFromCamera(mouseRef.current, cameraRef.current!)

      const meshes: THREE.Mesh[] = []
      sceneRef.current!.traverse((child) => {
        if (child instanceof THREE.Mesh && child.visible) meshes.push(child)
      })

      const intersects = raycasterRef.current.intersectObjects(meshes)
      if (intersects.length > 0) {
        onPointSelect({
          x: intersects[0].point.x,
          y: intersects[0].point.y,
          z: intersects[0].point.z
        })
      }
    }

    const handler = handleCanvasClick

    // Remove old event listeners
    if (clickHandlerRef.current) {
      rendererRef.current.domElement.removeEventListener('click', clickHandlerRef.current)
    }

    // Add new event listeners
    clickHandlerRef.current = handler
    rendererRef.current.domElement.addEventListener('click', handler)

    return () => {
      if (clickHandlerRef.current && rendererRef.current) {
        rendererRef.current.domElement.removeEventListener('click', clickHandlerRef.current)
      }
    }
  }, [selectedTool, onPointSelect, isEditingCalibrationLandmarks, onCalibrationLandmarkUpdate, pickingCalibrationPoint, onCalibrationPointPicked])

  // Animation loop
  useEffect(() => {
    if (!sceneRef.current || !cameraRef.current || !rendererRef.current) return

    const animate = () => {
      frameRef.current = requestAnimationFrame(animate)

      // Update controls
      if (controlsRef.current) {
        controlsRef.current.update()
      }

      rendererRef.current?.render(sceneRef.current!, cameraRef.current!)
    }

    animate()

    return () => {
      if (frameRef.current) {
        cancelAnimationFrame(frameRef.current)
      }
    }
  }, [])



  // Update object visibility based on props
  useEffect(() => {
    if (!sceneRef.current) return

    objects.forEach(obj => {
      const threeObject = sceneRef.current?.children.find(
        child => child.userData.id === obj.id
      )
      if (threeObject) {
        threeObject.visible = obj.visible
      }
    })
  }, [objects])

  // Handle calibration landmarks visualization
  useEffect(() => {
    if (!sceneRef.current) return

    // Get or create calibration landmarks group
    let calibrationGroup = sceneRef.current.getObjectByName('calibration-landmarks-group') as THREE.Group
    if (!calibrationGroup) {
      calibrationGroup = new THREE.Group()
      calibrationGroup.name = 'calibration-landmarks-group'
      sceneRef.current.add(calibrationGroup)
    }

    if (!showCalibrationLandmarks || calibrationLandmarks.length === 0) {
      calibrationGroup.visible = false
      return
    }

    calibrationGroup.visible = true

    // Clear existing calibration landmarks
    while (calibrationGroup.children.length > 0) {
      const child = calibrationGroup.children[0]
      calibrationGroup.remove(child)
      if (child instanceof THREE.Mesh) {
        child.geometry.dispose()
        ;(child.material as THREE.Material).dispose()
      }
    }

    // Create calibration landmark spheres with different colors
    calibrationLandmarks.forEach((landmark) => {
      if (!landmark.coordinates_3d ||
          landmark.coordinates_3d.x === undefined ||
          landmark.coordinates_3d.y === undefined ||
          landmark.coordinates_3d.z === undefined) {
        return
      }

      // Skip rendering the sphere that is currently being re-picked
      if (pickingCalibrationPoint === landmark.id) return

      const geometry = new THREE.SphereGeometry(0.3, 16, 16)

      let color = 0x0000ff // Blue - Heel
      if (landmark.landmark_type === 'MT1' || landmark.landmark_type === 'Big Toe') color = 0x00ff00 // Green
      if (landmark.landmark_type === 'MT5' || landmark.landmark_type === 'Pinky') color = 0xff0000 // Red

      const material = new THREE.MeshStandardMaterial({
        color,
        emissive: color,
        emissiveIntensity: 0.7,
        metalness: 0.5,
        roughness: 0.3
      })

      const mesh = new THREE.Mesh(geometry, material)
      mesh.position.set(
        landmark.coordinates_3d.x,
        landmark.coordinates_3d.y,
        landmark.coordinates_3d.z
      )
      mesh.userData = {
        landmarkId: landmark.id,
        landmarkType: landmark.landmark_type,
        isCalibrationLandmark: true,
        isEditable: isEditingCalibrationLandmarks
      }

      // In edit mode, make landmarks clickable via raycasting by
      // registering them with the click handler lookup.
      if (isEditingCalibrationLandmarks) {
        mesh.userData.selectable = true
      }

      calibrationGroup.add(mesh)
    })

    // When entering edit mode or picking mode, ensure OrbitControls don't steal clicks
    if (isEditingCalibrationLandmarks || pickingCalibrationPoint) {
      if (controlsRef.current) controlsRef.current.enabled = false
    } else {
      if (controlsRef.current) controlsRef.current.enabled = true
      // Leaving edit mode — detach transform controls and reset selection
      setSelectedCalibrationLandmarkId(null)
      const tc = transformControlsRef.current as any
      if (tc) {
        tc.detach()
        tc.enabled = false
        tc.visible = false
      }
    }
  }, [calibrationLandmarks, showCalibrationLandmarks, isEditingCalibrationLandmarks, pickingCalibrationPoint])

  // Handle template overlay (2D outline projected onto plantar plane)
  useEffect(() => {
    if (!sceneRef.current) return

    let group = sceneRef.current.getObjectByName('template-overlay-group') as THREE.Group
    if (!group) {
      group = new THREE.Group()
      group.name = 'template-overlay-group'
      sceneRef.current.add(group)
    }
    // Z-offset from the V2 align dialog ("Translate Z (mm)") is interpreted
    // as a translation along the foot's UP axis. The actual axis is detected
    // below from the STL bbox; default to scene Y until then.
    group.position.set(0, 0, 0)

    // Clear children
    while (group.children.length > 0) {
      const child = group.children[0]
      group.remove(child)
      const obj = child as THREE.Mesh | THREE.Line | THREE.Points
      if (obj.geometry) obj.geometry.dispose()
      if (obj.material) {
        const mat = obj.material as THREE.Material | THREE.Material[]
        if (Array.isArray(mat)) mat.forEach(m => m.dispose())
        else mat.dispose()
      }
    }

    if (!templateOverlay || !templateOverlay.visible || !templateOverlay.points || templateOverlay.points.length < 3) {
      group.visible = false
      return
    }

    group.visible = true

    // V2-aligned templates (originCentroid present) and legacy templates
    // both carry world-space mm points. The foot mesh is normalised to
    // scene units, so we ALWAYS need the mm->scene scale fit. World-space
    // mode just uses the original ICP centroid as the centering anchor so
    // user-applied translate offsets are preserved end-to-end.
    // Auto-fit overlay to the current STL bounding box, projected onto the
    // PLANTAR side (mesh bottom = min.z minus a small offset so the line is
    // visible). Template's longest dimension is aligned with the foot's
    // longest XY dimension — most patient scans are oriented heel→toe along
    // the longer axis, but a few are rotated 90°, so we orient on the fly.
    // Up-axis (foot height direction) and the two footprint axes are
    // detected from the foot bbox: the smallest extent is height, the
    // other two are width / length. This handles both Z-up STL exports
    // (typical CT/foot scanners) and Y-up STL exports (three.js default,
    // many FotoScan presets) without a manual flag.
    let centerU = 0   // first footprint axis center (scene coord)
    let centerV = 0   // second footprint axis center (scene coord)
    let planeH = 0    // up-axis level where the template line is drawn
    let scale = 1
    let rotateTemplate90 = false
    let upAxis: 'x' | 'y' | 'z' = 'y'
    let uAxis: 'x' | 'y' | 'z' = 'x'
    let vAxis: 'x' | 'y' | 'z' = 'z'
    if (stlMeshRef.current) {
      const bbox = new THREE.Box3().setFromObject(stlMeshRef.current)
      const sx = bbox.max.x - bbox.min.x
      const sy = bbox.max.y - bbox.min.y
      const sz = bbox.max.z - bbox.min.z
      // Pick the smallest-extent axis as up. A foot's height (~50 mm)
      // is much smaller than its length (~280 mm) and width (~110 mm).
      if (sx <= sy && sx <= sz) { upAxis = 'x'; uAxis = 'y'; vAxis = 'z' }
      else if (sz <= sx && sz <= sy) { upAxis = 'z'; uAxis = 'x'; vAxis = 'y' }
      else { upAxis = 'y'; uAxis = 'x'; vAxis = 'z' }
      const upMin = (bbox.min as any)[upAxis] as number
      const upMax = (bbox.max as any)[upAxis] as number
      const upRange = upMax - upMin
      const uMin = (bbox.min as any)[uAxis] as number
      const uMax = (bbox.max as any)[uAxis] as number
      const vMin = (bbox.min as any)[vAxis] as number
      const vMax = (bbox.max as any)[vAxis] as number
      const stlW = uMax - uMin
      const stlH = vMax - vMin
      const stlLong = Math.max(stlW, stlH)
      const stlShort = Math.min(stlW, stlH)

      // Detect which up-axis extremum is the PLANTAR (floor-touching) side.
      // The sole is the flattest, broadest part of the foot — so the bottom
      // 10% slab and the top 10% slab are compared by footprint-area in the
      // u-v plane. The slab with the LARGER area is plantar.
      let plantarAtMin = true
      try {
        const geom = (stlMeshRef.current as THREE.Mesh).geometry as THREE.BufferGeometry
        const posAttr = geom?.attributes?.position
        if (posAttr && posAttr.count > 200) {
          const stride = Math.max(1, Math.floor(posAttr.count / 8000))
          const m = stlMeshRef.current.matrixWorld
          const v = new THREE.Vector3()
          const lowCut = upMin + 0.10 * upRange
          const highCut = upMax - 0.10 * upRange
          let lUmin = Infinity, lUmax = -Infinity, lVmin = Infinity, lVmax = -Infinity
          let hUmin = Infinity, hUmax = -Infinity, hVmin = Infinity, hVmax = -Infinity
          for (let i = 0; i < posAttr.count; i += stride) {
            v.fromBufferAttribute(posAttr, i)
            v.applyMatrix4(m)
            const upV = (v as any)[upAxis] as number
            const uV = (v as any)[uAxis] as number
            const vV = (v as any)[vAxis] as number
            if (upV <= lowCut) {
              if (uV < lUmin) lUmin = uV
              if (uV > lUmax) lUmax = uV
              if (vV < lVmin) lVmin = vV
              if (vV > lVmax) lVmax = vV
            } else if (upV >= highCut) {
              if (uV < hUmin) hUmin = uV
              if (uV > hUmax) hUmax = uV
              if (vV < hVmin) hVmin = vV
              if (vV > hVmax) hVmax = vV
            }
          }
          const lowArea = (lUmax - lUmin) * (lVmax - lVmin)
          const highArea = (hUmax - hUmin) * (hVmax - hVmin)
          if (isFinite(lowArea) && isFinite(highArea) && Math.max(lowArea, highArea) > 0) {
            plantarAtMin = lowArea >= highArea * 0.95
          }
        }
      } catch {
        // keep default
      }
      const longOffset = 0.01 * (stlLong + 1)
      planeH = plantarAtMin ? upMin - longOffset : upMax + longOffset

      // Plantar centroid in the u-v footprint plane.
      centerU = (uMin + uMax) / 2
      centerV = (vMin + vMax) / 2
      try {
        const geom = (stlMeshRef.current as THREE.Mesh).geometry as THREE.BufferGeometry
        const posAttr = geom?.attributes?.position
        if (posAttr && posAttr.count > 50) {
          const upCut = plantarAtMin
            ? upMin + 0.3 * upRange
            : upMax - 0.3 * upRange
          const stride = Math.max(1, Math.floor(posAttr.count / 8000))
          const m = stlMeshRef.current.matrixWorld
          const v = new THREE.Vector3()
          let su = 0, sv = 0, n = 0
          for (let i = 0; i < posAttr.count; i += stride) {
            v.fromBufferAttribute(posAttr, i)
            v.applyMatrix4(m)
            const upV = (v as any)[upAxis] as number
            if (plantarAtMin ? upV > upCut : upV < upCut) continue
            su += (v as any)[uAxis] as number
            sv += (v as any)[vAxis] as number
            n++
          }
          if (n > 20) {
            centerU = su / n
            centerV = sv / n
          }
        }
      } catch {
        // keep bbox-center fallback
      }

      const tplPts = templateOverlay.points
      let tMinX = Infinity, tMaxX = -Infinity, tMinY = Infinity, tMaxY = -Infinity
      for (const p of tplPts) {
        if (p[0] < tMinX) tMinX = p[0]
        if (p[0] > tMaxX) tMaxX = p[0]
        if (p[1] < tMinY) tMinY = p[1]
        if (p[1] > tMaxY) tMaxY = p[1]
      }
      const tW = Math.max(1, tMaxX - tMinX)
      const tH = Math.max(1, tMaxY - tMinY)
      // Templates are stored with x=medial-lateral (short), y=heel→toe (long).
      // Rotate 90° if the foot's long axis is the u-axis so the template's
      // long axis aligns with it.
      rotateTemplate90 = stlW > stlH
      const tplLong = Math.max(tW, tH)
      const tplShort = Math.min(tW, tH)
      const REF_TEMPLATE_LONG_MM = 280
      const unitScale = stlLong / REF_TEMPLATE_LONG_MM
      const overflow = Math.max(
        (tplLong * unitScale) / stlLong,
        (tplShort * unitScale) / stlShort,
      )
      scale = overflow > 1.05 ? unitScale / overflow : unitScale
    }

    // Apply user Translate Z (mm) along the detected up-axis.
    const zOff = templateOverlay?.zOffset ?? 0
    group.position.set(0, 0, 0)
    ;(group.position as any)[upAxis] = zOff

    // Publish the scene-space anchor (in u-v-up scene coords) so the
    // TransformControls gizmo can sit on the visible loop.
    const anchorVec = new THREE.Vector3()
    ;(anchorVec as any)[uAxis] = centerU
    ;(anchorVec as any)[vAxis] = centerV
    ;(anchorVec as any)[upAxis] = planeH
    templateAnchorRef.current = { x: anchorVec.x, y: anchorVec.y, z: anchorVec.z }

    const colorHex = templateOverlay.color || '#22c55e'
    const color = new THREE.Color(colorHex)

    // Center template points around the ORIGINAL (un-adjusted) centroid so
    // that user-applied translation in the dialog is preserved end-to-end.
    // Falling back to the current points' centroid keeps legacy callers
    // (which never translate) working unchanged.
    const pts = templateOverlay.points
    const cx = templateOverlay.originCentroid
      ? templateOverlay.originCentroid[0]
      : pts.reduce((s, p) => s + p[0], 0) / pts.length
    const cy = templateOverlay.originCentroid
      ? templateOverlay.originCentroid[1]
      : pts.reduce((s, p) => s + p[1], 0) / pts.length

    // Project a template (mm, x-medial/y-heelToToe) point into scene coords
    // along the foot's u-v footprint plane at the plantar height.
    const project = (x: number, y: number): [number, number, number] => {
      const dx = (x - cx) * scale
      const dy = (y - cy) * scale
      const pu = rotateTemplate90 ? dy : dx
      const pv = rotateTemplate90 ? dx : dy
      const out = new THREE.Vector3()
      ;(out as any)[uAxis] = pu + centerU
      ;(out as any)[vAxis] = pv + centerV
      ;(out as any)[upAxis] = planeH
      return [out.x, out.y, out.z]
    }

    const verts: number[] = []
    for (const [x, y] of pts) {
      const [px, py, pz] = project(x, y)
      verts.push(px, py, pz)
    }
    // Close loop
    if (pts.length > 0) {
      const [px, py, pz] = project(pts[0][0], pts[0][1])
      verts.push(px, py, pz)
    }

    const lineGeom = new LineGeometry()
    lineGeom.setPositions(verts)
    const isSelected = selectedObjectId === 'v2-insole-cut'
    // Selection state: switch to amber and bump line width so the user
    // can clearly see which object G-Object Manager is highlighting.
    const renderColor = isSelected ? new THREE.Color('#fbbf24') : color
    // Line2 / LineMaterial linewidth is in pixels (resolution-aware).
    // Default 2 px; +10% bump (≈2.2 px) per UX request, 4 px when selected.
    const baseWidth = 2.2
    const selectedWidth = 4.0
    const lineMat = new LineMaterial({
      color: renderColor.getHex(),
      linewidth: isSelected ? selectedWidth : baseWidth,
      worldUnits: false,
      depthTest: true,
      transparent: false,
    })
    // LineMaterial NEEDS the renderer's drawing-buffer size to compute
    // pixel widths. Pull it from the renderer if available.
    const r = rendererRef.current
    if (r) {
      const size = new THREE.Vector2()
      r.getSize(size)
      lineMat.resolution.set(size.x, size.y)
    } else {
      lineMat.resolution.set(window.innerWidth, window.innerHeight)
    }
    const line = new Line2(lineGeom, lineMat)
    line.computeLineDistances()
    line.scale.set(1, 1, 1)
    line.userData = {
      id: 'v2-insole-cut',
      objectType: 'template',
      name: templateOverlay.name,
    }
    group.add(line)
    // Tag the parent group too so a raycast hit on the line can be
    // resolved back to the synthetic Object-Manager row.
    group.userData = {
      id: 'v2-insole-cut',
      objectType: 'template',
      name: templateOverlay.name,
    }

    // Landmark spheres — fixed ~3 mm radius mapped through unitScale.
    const sphereRadius = Math.max(0.02, 3.0 * scale)
    if (templateOverlay.landmarks) {
      Object.entries(templateOverlay.landmarks).forEach(([name, coord]) => {
        if (!coord || coord.length < 2) return
        const sphereGeom = new THREE.SphereGeometry(sphereRadius, 12, 12)
        const sphereMat = new THREE.MeshBasicMaterial({ color: 0xef4444 })
        const sphere = new THREE.Mesh(sphereGeom, sphereMat)
        const [px, py, pz] = project(coord[0], coord[1])
        sphere.position.set(px, py, pz)
        sphere.userData = { id: `template-landmark-${name}`, name }
        group.add(sphere)
      })
    }
  }, [templateOverlay, stlData, selectedObjectId])

  // Handle STL data changes (when loaded dynamically after initialization)
  useEffect(() => {
    // Only load if:
    // 1. Scene is initialized
    // 2. New stlData is provided
    // 3. It's different from the last loaded data
    if (!isInitialized || !stlData || stlData === lastStlDataRef.current) {
      return
    }

    console.log('Canvas3D: New STL data detected, loading...')
    loadSTLMesh(stlData)
    lastStlDataRef.current = stlData
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stlData, isInitialized])

  // Handle object selection highlighting
  useEffect(() => {
    if (!sceneRef.current) return

    // Reset all objects to default material
    sceneRef.current.children.forEach(child => {
      if (child instanceof THREE.Mesh && child.userData.id && child.userData.id !== 'point-marker') {
        // Reset to default material
        if (child.userData.id === 'stl-mesh') {
          const defaultMaterial = new THREE.MeshPhongMaterial({
            color: 0x00aaff,
            specular: 0x111111,
            shininess: 200,
            side: THREE.DoubleSide
          })

          // Dispose old material
          if (Array.isArray(child.material)) {
            child.material.forEach(mat => mat.dispose())
          } else {
            child.material.dispose()
          }

          child.material = defaultMaterial
        }
      }
    })

    // Highlight selected object
    if (selectedObjectId) {
      const selectedObject = sceneRef.current.children.find(
        child => child.userData.id === selectedObjectId
      )

      if (selectedObject && selectedObject instanceof THREE.Mesh) {
        const highlightMaterial = new THREE.MeshPhongMaterial({
          color: 0xffaa00,
          specular: 0x111111,
          shininess: 200,
          side: THREE.DoubleSide,
          emissive: 0xff6600,
          emissiveIntensity: 0.3
        })

        // Dispose old material
        if (Array.isArray(selectedObject.material)) {
          selectedObject.material.forEach(mat => mat.dispose())
        } else {
          selectedObject.material.dispose()
        }

        selectedObject.material = highlightMaterial
        console.log('Canvas3D: Object highlighted:', selectedObjectId)
      }
    }
  }, [selectedObjectId])

  // TransformControls — attach to a proxy positioned at the V2 template centroid
  // when the insole-cut template is selected. The proxy emits position deltas
  // back to the V2 dialog, which owns the slider state.
  useEffect(() => {
    const tc = transformControlsRef.current as any
    if (!tc || !sceneRef.current) return

    const isV2Selected = selectedObjectId === 'v2-insole-cut'
    const overlayGroup = sceneRef.current.getObjectByName('template-overlay-group') as THREE.Group | undefined

    if (isV2Selected && overlayGroup) {
      // Use the scene-space anchor published by the overlay-render effect.
      // Falling back to a raw-mm centroid if no anchor (the overlay was not
      // rendered yet) would place the gizmo off-screen at mm coordinates,
      // which is the bug we are fixing here.
      const anchor = templateAnchorRef.current
      if (!anchor) {
        tc.detach()
        tc.enabled = false
        tc.visible = false
        if (transformControlsHelperRef.current) transformControlsHelperRef.current.visible = false
        return
      }
      let proxy = transformProxyRef.current
      if (!proxy) {
        proxy = new THREE.Object3D()
        proxy.name = 'transform-proxy'
        sceneRef.current.add(proxy)
        transformProxyRef.current = proxy
      }
      // Position proxy at the rendered template centroid in scene units,
      // including the group's up-axis offset (Translate Z mm).
      proxy.position.set(
        anchor.x + overlayGroup.position.x,
        anchor.y + overlayGroup.position.y,
        anchor.z + overlayGroup.position.z,
      )
      proxy.rotation.set(0, 0, 0)
      proxy.scale.set(1, 1, 1)
      tc.attach(proxy)
      tc.enabled = true
      tc.visible = true
      if (transformControlsHelperRef.current) transformControlsHelperRef.current.visible = true
    } else {
      tc.detach()
      tc.enabled = false
      tc.visible = false
      if (transformControlsHelperRef.current) transformControlsHelperRef.current.visible = false
    }
  }, [selectedObjectId, templateOverlay])

  // Update gizmo mode (translate/rotate/scale) on prop change.
  //  - translate: all three axes (X/Y/Z) — Z lifts the template off the
  //    plantar plane (matches the V2 dialog's Translate Z slider).
  //  - rotate: Z ring only — the only meaningful rotation for a planar
  //    insole template; X/Y rings would tilt it out of plane.
  //  - scale: X/Y only — uniform-ish scaling in the template plane.
  useEffect(() => {
    const tc = transformControlsRef.current as any
    if (!tc) return
    tc.setMode(transformMode)
    if (transformMode === 'rotate') {
      tc.showX = false
      tc.showY = false
      tc.showZ = true
    } else if (transformMode === 'scale') {
      tc.showX = true
      tc.showY = true
      tc.showZ = false
    } else {
      // translate
      tc.showX = true
      tc.showY = true
      tc.showZ = true
    }
  }, [transformMode])

  // Create visual markers for selected points (red)
  useEffect(() => {
    if (!sceneRef.current) return

    // Remove existing point markers
    const existingMarkers = sceneRef.current.children.filter(
      child => child.userData.type === 'point-marker'
    )
    existingMarkers.forEach(marker => {
      sceneRef.current?.remove(marker)
      if (marker instanceof THREE.Mesh) {
        marker.geometry.dispose()
        if (Array.isArray(marker.material)) {
          marker.material.forEach(mat => mat.dispose())
        } else {
          marker.material.dispose()
        }
      }
    })

    // Add new point markers (red)
    selectedPoints.forEach(point => {
      const sphereGeometry = new THREE.SphereGeometry(0.05, 16, 16)
      const sphereMaterial = new THREE.MeshBasicMaterial({
        color: 0xff0000, // Red for point tool
        transparent: true,
        opacity: 0.8
      })
      const sphere = new THREE.Mesh(sphereGeometry, sphereMaterial)
      sphere.position.set(point.position.x, point.position.y, point.position.z)
      sphere.userData = {
        type: 'point-marker',
        pointId: point.id,
        selectable: false
      }
      sceneRef.current?.add(sphere)
    })
  }, [selectedPoints])

  // Create visual markers for measure points (blue) and distance line
  useEffect(() => {
    if (!sceneRef.current) return

    // Remove existing measure markers and lines
    const existingMeasureMarkers = sceneRef.current.children.filter(
      child => child.userData.type === 'measure-marker' || child.userData.type === 'measure-line'
    )
    existingMeasureMarkers.forEach(marker => {
      sceneRef.current?.remove(marker)
      if (marker instanceof THREE.Mesh) {
        marker.geometry.dispose()
        if (Array.isArray(marker.material)) {
          marker.material.forEach(mat => mat.dispose())
        } else {
          marker.material.dispose()
        }
      } else if (marker instanceof THREE.Line) {
        marker.geometry.dispose()
        if (Array.isArray(marker.material)) {
          marker.material.forEach(mat => mat.dispose())
        } else {
          marker.material.dispose()
        }
      }
    })

    // Add new measure point markers (blue)
    measurePoints.forEach(point => {
      const sphereGeometry = new THREE.SphereGeometry(0.06, 16, 16) // Slightly larger
      const sphereMaterial = new THREE.MeshBasicMaterial({
        color: 0x0088ff, // Blue for measure tool
        transparent: true,
        opacity: 0.9
      })
      const sphere = new THREE.Mesh(sphereGeometry, sphereMaterial)
      sphere.position.set(point.position.x, point.position.y, point.position.z)
      sphere.userData = {
        type: 'measure-marker',
        pointId: point.id,
        selectable: false
      }
      sceneRef.current?.add(sphere)
    })

    // Add line between measure points if we have 2
    if (measurePoints.length === 2) {
      const points = [
        new THREE.Vector3(measurePoints[0].position.x, measurePoints[0].position.y, measurePoints[0].position.z),
        new THREE.Vector3(measurePoints[1].position.x, measurePoints[1].position.y, measurePoints[1].position.z)
      ]
      const lineGeometry = new THREE.BufferGeometry().setFromPoints(points)
      const lineMaterial = new THREE.LineBasicMaterial({
        color: 0x00ff00, // Green line
        linewidth: 2
      })
      const line = new THREE.Line(lineGeometry, lineMaterial)
      line.userData = {
        type: 'measure-line',
        selectable: false
      }
      sceneRef.current?.add(line)
    }
  }, [measurePoints])

  // Auto-save state when important changes occur
  useEffect(() => {
    if (!cameraRef.current || !sceneRef.current) return

    const saveState = () => {
      if (cameraRef.current) {
        autoSaveViewerState(
          cameraRef.current,
          selectedPoints,
          objects,
          selectedTool
        )
      }
    }

    // Save state when selectedPoints, objects, or selectedTool change
    saveState()
  }, [selectedPoints, objects, selectedTool])

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      if (!mountRef.current || !cameraRef.current || !rendererRef.current) return

      const width = mountRef.current.clientWidth
      const height = mountRef.current.clientHeight

      cameraRef.current.aspect = width / height
      cameraRef.current.updateProjectionMatrix()
      rendererRef.current.setSize(width, height)
      rendererRef.current.setPixelRatio(Math.min(window.devicePixelRatio, 3))

      // Keep Line2 / LineMaterial line widths pixel-correct.
      const overlay = sceneRef.current?.getObjectByName('template-overlay-group') as THREE.Group | undefined
      overlay?.children.forEach(child => {
        const mat = (child as Line2).material as LineMaterial | undefined
        if (mat && (mat as any).resolution) {
          (mat as any).resolution.set(width, height)
        }
      })
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  if (error) {
    return (
      <div className="canvas-3d-container error">
        <div className="error-message">
          <h3>3D Scene Error</h3>
          <p>{error}</p>
          <button onClick={() => window.location.reload()}>Reload</button>
        </div>
      </div>
    )
  }

  // Always render the mount div, show loading overlay if needed

  return (
    <div className="canvas-3d-container">
      <div ref={mountRef} className="three-js-mount" />

      {isLoading && (
        <div className="canvas-overlay" style={{ backgroundColor: 'rgba(42, 42, 42, 0.9)', pointerEvents: 'auto', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div className="loading-message">
            <h3>Loading 3D Scene...</h3>
            <p>Initializing Three.js renderer...</p>
          </div>
        </div>
      )}

      {!isLoading && (
        <div className="canvas-overlay">
          <div className="canvas-info">
            <div>Tool: {selectedTool || 'None'}</div>
            <div>Objects: {objects.filter(obj => obj.visible).length} visible</div>
            <div>Points: {selectedPoints.length} selected</div>
            {selectedTool === 'measure' && (
              <div style={{ color: '#00ff00', fontWeight: 'bold' }}>
                {measurePoints.length === 0 && 'Select first point'}
                {measurePoints.length === 1 && 'Select second point'}
                {measurePoints.length === 2 && measureDistance !== null &&
                  `Distance: ${measureDistance.toFixed(2)} mm`
                }
              </div>
            )}
            <div>Engine: Three.js {THREE.REVISION}</div>
          </div>

          <div className="canvas-controls">
            <div className="control-hint">
              {selectedTool === 'point'
                ? 'Click on 3D surfaces to select points'
                : selectedTool === 'measure'
                ? 'Click 2 points on the mesh to measure distance'
                : selectedTool === 'move'
                ? 'Left-click drag to pan the view'
                : selectedTool === 'rotate'
                ? 'Left-click drag to rotate the view'
                : 'Mouse: Orbit • Wheel: Zoom • Right-click: Pan'
              }
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
